<template>
  <div
    class="inline-flex items-center group"
    :class="[
      { 'opacity-60': disabled },
      getPtClass<PassThroughMeridiaToggle>('wrapper', passThrough),
    ]"
  >
    <PrimeToggleSwitch
      v-model="modelValue"
      unstyled
      :pt="passThrough"
      :disabled="disabled"
      :input-id="computedId"
    />
    <label
      v-if="label"
      :for="computedId"
      class="text-neutral-text-body text-body-base pl-xs flex items-center transition-all duration-200"
      :class="[
        {
          'text-neutral-text-disabled cursor-not-allowed': disabled,
          'cursor-pointer group-hover:text-neutral-text-subtitle': !disabled,
        },
        getPtClass<PassThroughMeridiaToggle>('label', passThrough),
      ]"
    >
      <slot v-if="$slots.label" name="label" :label="label" />
      <span v-else>{{ label }}</span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PassThroughMeridiaToggle, ToggleSize } from './types';
import { usePassThrough } from 'primevue/passthrough';
import _ from 'lodash';
import { getPtClass } from '../utils';
import PrimeToggleSwitch from 'primevue/toggleswitch';

const modelValue = defineModel<boolean>();

const props = withDefaults(
  defineProps<{
    size?: `${ToggleSize}`;
    label?: string;
    disabled?: boolean;
    pt?: PassThroughMeridiaToggle;
  }>(),
  {
    size: ToggleSize.BASE,
    disabled: false,
  }
);

defineEmits<{
  (event: 'update:model-value', value: boolean): void;
}>();

defineSlots<{
  label: (label?: string) => any;
}>();

const computedId = computed(() => _.uniqueId('toggle-'));

const sizeConfig = {
  // Needed for map layers, discuss with Tali addition to the design system
  [ToggleSize.SMALL]: {
    root: 'min-w-[22px] h-[13px]',
    slider: 'h-[10px] top-[1.5px]',
    handle: 'w-[13px] h-[13px]',
    handleOn: 'left-[9px]',
  },
  [ToggleSize.BASE]: {
    root: 'min-w-[28px] h-[16px]',
    slider: 'h-[12px] top-[2px]',
    handle: 'w-[16px] h-[16px]',
    handleOn: 'left-[12px]',
  },
  [ToggleSize.LARGE]: {
    root: 'min-w-[36px] h-[20px]',
    slider: 'h-[15px] top-[2.5px]',
    handle: 'w-[20px] h-[20px]',
    handleOn: 'left-[16px]',
  },
};

const currentSize = computed(() => sizeConfig[props.size as ToggleSize]);

const passThrough = usePassThrough(
  props.pt,
  {
    root: () => ({
      class: ['relative inline-flex', currentSize.value.root],
    }),
    input: (options) => ({
      class: [
        'appearance-none absolute inset-0 p-0 m-0 opacity-0 z-[1] outline-none border-0',
        {
          'cursor-pointer': !options.context.disabled,
          'cursor-not-allowed': options.context.disabled,
        },
      ],
    }),
    slider: (options) => ({
      class: [
        'absolute rounded-full transition-all duration-200 left-0 right-0',
        currentSize.value.slider,
        options.context.checked
          ? 'bg-primary-surface-subtle/[0.54]'
          : 'bg-neutral-surface-disabled/[0.54]',
      ],
    }),
    handle: (options) => ({
      class: [
        'absolute top-1/2 -translate-y-1/2 rounded-full transition-all duration-200',
        currentSize.value.handle,
        options.context.checked
          ? `${currentSize.value.handleOn} bg-primary-surface-lighter`
          : 'left-0 bg-neutral-border-disabled',
        !options.context.disabled && [
          'group-hover:scale-[1.15]',
        ],
      ],
    }),
  } as PassThroughMeridiaToggle,
  { mergeSections: true, mergeProps: true }
) as PassThroughMeridiaToggle;
</script>
